/**
 * <collection-circles>
 *
 * Shows a horizontally scrollable row of collection circles. When the row is
 * wider than the viewport a mobile scroll indicator (track + thumb) is revealed
 * so users can see the scroll position and how much is left. The element only
 * updates styles and the `hidden` attribute – the markup lives in the section.
 */
class CollectionCircles extends HTMLElement {
  connectedCallback() {
    this.scrollArea = this.querySelector('.scroll-area');
    console.log("scrollARea", this.scrollArea.scrollLeft);
    this.scrollArea.scrollLeft = 0;
    this.scrollbar = this.querySelector('.collection-circles__scrollbar');
    this.thumb = this.querySelector('.collection-circles__scrollbar-thumb');

    if (!this.scrollArea || !this.scrollbar || !this.thumb) return;

    this.rtl = Boolean(window.theme && theme.config && theme.config.rtl);
    this.ticking = false;
    this.dragging = false;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
    this.onThumbPointerMove = this.onThumbPointerMove.bind(this);
    this.onThumbPointerUp = this.onThumbPointerUp.bind(this);

    this.scrollArea.addEventListener('scroll', this.onScroll, { passive: true });
    this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);
    window.addEventListener('resize', this.update);


    this.update();
  }

  disconnectedCallback() {
    if (this.scrollArea) this.scrollArea.removeEventListener('scroll', this.onScroll);
    if (this.thumb) this.thumb.removeEventListener('pointerdown', this.onThumbPointerDown);
    window.removeEventListener('resize', this.update);
  }

  onScroll() {
    // Throttle scroll updates to one per animation frame.
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateThumbPosition();
      this.ticking = false;
    });
  }

  update() {
    const scrollableDistance = this.scrollArea.scrollWidth - this.scrollArea.clientWidth;

    // Nothing to scroll – keep the indicator hidden.
    if (scrollableDistance <= 0) {
      this.scrollbar.setAttribute('hidden', '');
      return;
    }

    this.scrollbar.removeAttribute('hidden');

    // Thumb width mirrors the visible portion of the track.
    const trackWidth = this.scrollbar.clientWidth;
    const visibleRatio = this.scrollArea.clientWidth / this.scrollArea.scrollWidth;
    this.thumbWidth = trackWidth * visibleRatio;
    this.maxOffset = trackWidth - this.thumbWidth;

    this.thumb.style.width = `${this.thumbWidth}px`;
    this.updateThumbPosition();
  }

  updateThumbPosition() {
    if (this.scrollbar.hasAttribute('hidden')) return;

    const scrollableDistance = this.scrollArea.scrollWidth - this.scrollArea.clientWidth;
    if (scrollableDistance <= 0) return;

    // In RTL scrollLeft is negative, so normalise it to a positive distance.
    const scrolled = this.scrollArea.scrollLeft * (this.rtl ? -1 : 1);
    const progress = scrolled / scrollableDistance;
    const offset = progress * this.maxOffset;

    console.log('updateThumbPosition', { scrollLeft: this.scrollArea.scrollLeft, rtl: this.rtl, scrolled, progress, offset, maxOffset: this.maxOffset });

    this.thumb.style.transform = `translateX(${this.rtl ? -offset : offset}px)`;
  }

  // Dragging the thumb moves the row, so the indicator doubles as a scrollbar.
  onThumbPointerDown(event) {
    if (this.scrollbar.hasAttribute('hidden') || this.maxOffset <= 0) return;

    event.preventDefault();
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.scrollArea.scrollLeft;

    this.thumb.setPointerCapture(event.pointerId);
    this.thumb.addEventListener('pointermove', this.onThumbPointerMove);
    this.thumb.addEventListener('pointerup', this.onThumbPointerUp);
    this.thumb.addEventListener('pointercancel', this.onThumbPointerUp);
  }

  onThumbPointerMove(event) {
    if (!this.dragging) return;

    const scrollableDistance = this.scrollArea.scrollWidth - this.scrollArea.clientWidth;
    if (scrollableDistance <= 0) return;

    // Translate the thumb travel into scroll distance via the track/content ratio.
    const deltaX = event.clientX - this.dragStartX;
    const scrollDelta = deltaX * (scrollableDistance / this.maxOffset) * (this.rtl ? -1 : 1);
    const target = this.dragStartScrollLeft + scrollDelta;

    // scrollLeft runs 0..distance in LTR and -distance..0 in RTL.
    const min = this.rtl ? -scrollableDistance : 0;
    const max = this.rtl ? 0 : scrollableDistance;
    this.scrollArea.scrollLeft = Math.min(Math.max(target, min), max);
  }

  onThumbPointerUp(event) {
    this.dragging = false;
    this.thumb.releasePointerCapture(event.pointerId);
    this.thumb.removeEventListener('pointermove', this.onThumbPointerMove);
    this.thumb.removeEventListener('pointerup', this.onThumbPointerUp);
    this.thumb.removeEventListener('pointercancel', this.onThumbPointerUp);
  }
}

if (!customElements.get('collection-circles')) {
  customElements.define('collection-circles', CollectionCircles);
}
